import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';

import dropzone from 'assets/images/dropzone.png';

import arrowDownDash from 'assets/icons/arrow-down-dash.svg';
import { useTranslation } from 'react-i18next';
import { CircularProgress as MuiCircularProgress, useTheme } from '@mui/material';
import PreviewFilesList from 'components/PreviewFilesList';

const sizeError = {
  code: 'size-is-big',
  title: `Be aware, file exceed the maximum size`,
  subtitleFirstPart: 'Please change, or reduce.',
  subtitleSecondPart: 'Size maximum allowed is 10Mb',
};

const nameError = {
  code: 'name-is-same',
  title: `File with this name already exist`,
  subtitleFirstPart: 'Please change a name',
  subtitleSecondPart: 'Name must be unique',
};

const typeError = {
  code: 'type-is-not-valid',
  title: `File with this type is not accepted`,
  subtitleFirstPart: 'Please change',
  subtitleSecondPart:
    'Accepted only image/jpeg, image/jpg, image/png, application/pdf, video/mp4',
};

const acceptedTypesString =
  'image/jpeg,image/jpg,image/png,application/pdf,video/mp4';

const Dropzone = ({
  onLoadFile,
  files,
  onDelete,
  onShowValidationError,
  errors,
  onSetValidationError,
  isInput = false,
  withTitle,
  placeholder,
  postPlaceholder,
  formInput,
  acceptedTypesProps = acceptedTypesString,
  maxFiles = null,
  singleFile = false,
  helperText,
  mainTextArray,
  loading,
  customValidation,
}) => {
  const { t } = useTranslation();
  const theme = useTheme()
  const onDrop = useCallback(
    async (acceptedFiles) => {
      const customErrorArray = [];
      const uploadedFiles = [];
      for (const f of acceptedFiles) {
        let customError = null;
        if (customValidation) {
          customError = await customValidation(f);
        }
        if (customError) {
          customErrorArray.push(customError);
        } else {
          Object.assign(f, {
            preview: URL.createObjectURL(f),
            id: `${f.name}-${f.lastModified}`,
          });
          uploadedFiles.push(f);
        }
      }
      if (customErrorArray.length) {
        onSetValidationError(customErrorArray[0]);
        onShowValidationError(true);
      }
      
      onLoadFile(uploadedFiles);
      if (formInput) {
        if (singleFile) {
          formInput.onChange(uploadedFiles);
        } else {
          formInput.onChange([...formInput.value, ...uploadedFiles]);
        }
      }
    },
    [
      onLoadFile,
      formInput,
      customValidation,
      onSetValidationError,
      onShowValidationError,
    ]
  );
  const onDeleteClick = (file) => {
    onDelete(file);
    if (formInput)
      formInput.onChange(formInput.value.filter((f) => f.name !== file.title));
  };

  const isValidWeight = (size) => (size / (1024 * 1024)).toFixed(2) * 1 < 10;
  const isAccepted = (type) => acceptedTypesProps.split(',').includes(type);

  const validator = (file) => {
    if (!isValidWeight(file.size)) {
      onSetValidationError(sizeError);
      onShowValidationError(true);
      return sizeError;
    }
    if (!isAccepted(file.type)) {
      onSetValidationError({
        ...typeError,
        subtitleSecondPart: acceptedTypesProps,
      });
      onShowValidationError(true);
      return typeError;
    }
    if (files.find((f) => f.name === file.name)) {
      onSetValidationError(nameError);
      onShowValidationError(true);
      return nameError;
    }
    return null;
  };

  const options = {
    onDrop,
    accept: acceptedTypesProps,
    validator: validator,
  };

  const { getRootProps, getInputProps } = useDropzone(
    singleFile
      ? { ...options, maxFiles: 1 }
      : maxFiles
      ? { ...options, maxFiles }
      : options
  );

  return (
    <SContainer>
      <SDropzoneWrapper {...getRootProps()}>
        <input {...getInputProps()} accept={acceptedTypesProps} />
        {isInput ? (
          <>
            <SInputLabel withPrefix={true}>
              <SIcon src={arrowDownDash} />
              <SSpan style={{ color: theme.palette.colors.gray }}>
                {withTitle && t(placeholder)}
                {postPlaceholder ? (
                  <PostPlaceholder>{postPlaceholder}</PostPlaceholder>
                ) : (
                  t(placeholder)
                )}
              </SSpan>
            </SInputLabel>
            {maxFiles ? (
              <SInfo>
                {t('Maximum number of files is {{maxFiles}}', { maxFiles })}
              </SInfo>
            ) : (
              ''
            )}
          </>
        ) : (
          <SDropzone>
            <SImg src={dropzone} />
            <SDropzoneTitle>
              {t('Drag & drop')}
              {mainTextArray?.map?.((item, idx) =>
                idx === mainTextArray.length - 1 ? (
                  <>
                    {' '}
                    <span>{t(item)}</span>
                  </>
                ) : (
                  <>
                    {' '}
                    <span>{t(item)}</span>
                    {', '}
                  </>
                )
              )}
            </SDropzoneTitle>
            <SDropzoneSubTitle>
              {t('Or')} <span>{t('browse files')}</span> {t('on your computer')}
              <p>{t(helperText)}</p>
              {maxFiles ? (
                <p>
                  {t('Maximum number of files is {{maxFiles}}', { maxFiles })}
                </p>
              ) : null}
            </SDropzoneSubTitle>
          </SDropzone>
        )}
      </SDropzoneWrapper>
      {errors && errors.files ? <SError>{errors.files}</SError> : ''}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <MuiCircularProgress size={35} style={{ marginTop: '10px' }} />
        </div>
      ) : files && files.length ? (
        <PreviewFilesList files={files} onDelete={onDeleteClick} />
      ) : (
        ''
      )}
    </SContainer>
  );
};

const SContainer = styled.div``;
const SDropzoneWrapper = styled.div`
  cursor: pointer;
`;

const SInfo = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${({theme}) => theme.palette.colors.gray400};
  text-align: right;
`;

const SDropzone = styled.div`
  padding: 30px;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%232164E8FF' stroke-width='4' stroke-dasharray='6%2c 14' stroke-dashoffset='23' stroke-linecap='square'/%3e%3c/svg%3e");
`;
const SImg = styled.img`
  display: block;
  width: 150px;
  height: 90px;
`;

const SDropzoneTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${({theme}) => theme.palette.colors.black100};
  text-align: center;
  span {
    color: ${({theme}) => theme.palette.colors.blue100};
  }
  p {
    font-size: 15px;
    opacity: 0.5;
    font-weight: 400;
  }
`;

const SError = styled.div`
  color: ${({theme}) => theme.palette.colors.red100};
  margin: 5px 0;
`;

const SDropzoneSubTitle = styled(SDropzoneTitle)`
  font-weight: 500;
`;

const SInputLabel = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: ${({ withPrefix }) => (withPrefix ? '9px' : '14px')};
  border-radius: 5px;
  height: 45px;
  border: 1px solid ${({theme}) => theme.palette.colors.gray200};
  transition: all 0.3s;

  &:hover {
    cursor: pointer;
    border-color: black;
  }
`;
const SSpan = styled.span`
  font-weight: 500;
  font-size: 14px;
  color: ${({theme}) => theme.palette.colors.gray500};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const SIcon = styled.img`
  margin-right: 5px;
`;
const PostPlaceholder = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: 22px;
  opacity: 0.4;
  color: ${({theme}) => theme.palette.colors.gray500};
  margin-left: 5px;
`;

export default Dropzone;
